import React from 'react'
import { FormCheckboxField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Language } from 'api/Clients/Types/Language'
import { UpdateUserTeamDto } from 'api/Identity/Types/UpdateUserTeamDto'
import { UserTeamDto } from 'api/Identity/Types/UserTeamDto'
import { Form, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

interface AdministrationEditUserTeamFormValues {
  userName: string
  teamName: string
  autoAssignment: boolean
  communicationLanguage: number
  visibility: boolean
}

const AdministrationEditUserTeamFormUI: React.FC<
  FormikProps<AdministrationEditUserTeamFormValues> & OuterProps
> = (props) => {
  const { values, languages, handleSubmit, setFieldValue, isSubmitting, onClose } = props

  const handleCommunicationLanguageChange = (communicationLanguage: string | number) => {
    setFieldValue('communicationLanguage', Number(communicationLanguage) || 0)
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User Team</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormReadonlyField name='userName' label='User' value={values.userName} />
            <FormReadonlyField name='teamName' label='Team' value={values.teamName} />
            <FormCheckboxField name='autoAssignment' label='Auto Assignment' />
            <FormCheckboxField name='visibility' label='Visibility' />
            <FormSelectField
              name='communicationLanguage'
              label='Communication Language'
              options={[
                { id: 0, name: 'None' },
                ...languages.map((language) => ({
                  id: language.id,
                  name: language.name,
                })),
              ]}
              value={values.communicationLanguage}
              onChange={handleCommunicationLanguageChange}
              dataTest='communication-language-field'
              showSearch
              allowClear
            />
          </div>
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={() => handleSubmit()} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const dataToSubmit = (
  values: AdministrationEditUserTeamFormValues,
  data: UserTeamDto
): FormSubmitValues<UpdateUserTeamDto> => {
  return {
    id: data.id,
    eligibleToClientAutoAssignment: values.autoAssignment,
    languageId: values.communicationLanguage || null,
    visibility: values.visibility || false,
  }
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data: UserTeamDto
  languages: Language[]
  onSubmit(values: FormSubmitValues<UpdateUserTeamDto>): Promise<void>
}

export const AdministrationEditUserTeamForm = withFormik<
  OuterProps,
  AdministrationEditUserTeamFormValues
>({
  mapPropsToValues: ({ data }) => {
    return {
      userName: data.user.name,
      teamName: data.team.name || '',
      autoAssignment: data.autoAssignmentEnabled,
      communicationLanguage: data.language?.id || 0,
      visibility: data.visibility || false,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.data))
    } finally {
      setSubmitting(false)
    }
  },
  isInitialValid: false,
  enableReinitialize: true,
})(AdministrationEditUserTeamFormUI)

export default AdministrationEditUserTeamForm
